<template>
  <!-- KPI -->
  <KpiWidget
    v-if="widget.type === 'kpi'"
    :title="tConfig(widget.params.title, lang)"
    :value="kpiValue"
    :metric-value="kpiMetric.value"
    :metric-label="kpiMetric.label"
    :metric-only="kpiMetric.only"
    :format="widget.params.euro ? 'euro0' : null"
  />

  <!-- Gràfic ApexCharts (bar/line/area/donut/pie) -->
  <ChartWidget
    v-else-if="widget.type === 'chart' && widget.params.chart?.type !== 'table'"
    :options="options"
    :series="series"
    :allow-type-change="widget.params.allowTypeChange"
    :editable="editable"
    :lang="lang"
    @data-point-selection="(config) => $emit('data-point-selection', config)"
  />

  <!-- Vista de taula per a widgets chart amb chart.type = table -->
  <div v-else-if="widget.type === 'chart' && widget.params.chart?.type === 'table'" class="table-widget">
    <div class="table-header">
      <h3 v-if="widget.params.title?.text || widget.params.title" class="table-title">
        {{ tConfig(widget.params.title?.text || widget.params.title, lang) }}
      </h3>
      <button class="table-export-btn" @click="$emit('export-table', widget)">{{ t('exportXlsx', lang) }}</button>
    </div>
    <!-- Columnes arbitràries: params.columns = [{ field, label }] -->
    <table v-if="widget.params.columns" class="data-table">
      <thead>
        <tr>
          <th v-for="(col, ci) in widget.params.columns" :key="ci">{{ tConfig(col.label, lang) }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in (dataNode?.data || [])" :key="idx">
          <td v-for="(col, ci) in widget.params.columns" :key="ci">
            {{ getTableCellValue(item, col.field, dataNode?.domainLabels, widget.params.euro) }}
          </td>
        </tr>
      </tbody>
    </table>
    <table v-else class="data-table">
      <thead>
        <tr>
          <th v-if="effMapping.category">{{ effMapping.category }}</th>
          <th v-if="effMapping.value">{{ effMapping.value }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in (dataNode?.data || [])" :key="idx">
          <td v-if="effMapping.category">
            {{ getTableCellValue(item, effMapping.category, dataNode?.domainLabels) }}
          </td>
          <td v-if="effMapping.value">
            {{ getTableCellValue(item, effMapping.value, dataNode?.domainLabels, widget.params.euro) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <MapWidget
    v-else-if="widget.type === 'map'"
    :data="dataNode?.data || []"
    :map-key-field="widget.params?.mapKeyField || 'NOMCOMAR'"
    :data-key-field="widget.params?.dataKeyField || effMapping.category || 'comarca'"
    :value-field="widget.params?.valueField || 'vehicles'"
    :title="tConfig(widget.params?.title, lang)"
    :subtitle="tConfig(widget.params?.subtitle, lang)"
    :metrics="widget.params?.metrics || []"
    :metric-labels="metaLabels"
    :lang="lang"
  />

  <WorldMapWidget
    v-else-if="widget.type === 'world-map'"
    :data="dataNode?.data || []"
    :data-key-field="widget.params?.dataKeyField || effMapping.category || 'country'"
    :value-field="widget.params?.valueField || effMapping.value || 'count'"
    :title="tConfig(widget.params?.title?.text || widget.params?.title, lang)"
    :subtitle="tConfig(widget.params?.subtitle, lang)"
    :country-label="tConfig(widget.params?.countryLabel, lang)"
    :value-label="tConfig(widget.params?.valueLabel, lang)"
    :metrics="widget.params?.metrics || []"
    :metric-labels="metaLabels"
    :lang="lang"
  />

  <TextWidget
    v-else-if="widget.type === 'text'"
    :title="tConfig(widget.params.title, lang)"
    :body="widget.params.body"
    :lang="lang"
  />

  <PivotTableWidget
    v-else-if="widget.type === 'pivot'"
    :title="tConfig(widget.params.title?.text || widget.params.title, lang)"
    :data="dataNode?.data || []"
    :levels="Array.isArray(widget.params.dimension) ? widget.params.dimension : []"
    :row-field="effMapping.category"
    :col-field="effMapping.group"
    :value-field="effMapping.value || 'count'"
    :row-label="tConfig(widget.params.rowLabel, lang)"
    :value-label="tConfig(widget.params.valueLabel, lang)"
    :metrics="widget.params?.metrics || []"
    :metric-labels="metaLabels"
    :euro="widget.params?.euro"
  />

  <TreeTableWidget
    v-else-if="widget.type === 'tree-table'"
    :title="tConfig(widget.params.title?.text || widget.params.title, lang)"
    :data="dataNode?.data || []"
    :levels="widget.params.levels || []"
    :value-field="effMapping.value || 'count'"
    :row-label="tConfig(widget.params.rowLabel, lang)"
    :value-label="tConfig(widget.params.valueLabel, lang)"
    :domain-labels="dataNode?.domainLabels || {}"
    :euro="widget.params?.euro"
  />

  <MatrixTableWidget
    v-else-if="widget.type === 'matrix-table'"
    :title="tConfig(widget.params.title?.text || widget.params.title, lang)"
    :data="dataNode?.data || []"
    :row-field="effMapping.category"
    :col-field="effMapping.group"
    :value-field="effMapping.value || 'count'"
    :row-label="tConfig(widget.params.rowLabel, lang)"
    :value-label="tConfig(widget.params.valueLabel, lang)"
    :metrics="widget.params?.metrics || []"
    :metric-labels="metaLabels"
  />
</template>

<script setup>
// Dispatch de render d'UN widget segons el seu type. Extret de Dashboard.vue perquè el
// comparteixin el dashboard (dins del seu wrapper amb toolbar/drill) i el preview en viu
// de l'editor. Rep per props tot el que el pare calcula (options/series/dataNode/kpi*) i
// emet les interaccions cap amunt; així no depèn de l'estat de cap instància.
import ChartWidget from './ChartWidget.vue';
import MapWidget from './MapWidget.vue';
import WorldMapWidget from './WorldMapWidget.vue';
import KpiWidget from './KpiWidget.vue';
import TextWidget from './TextWidget.vue';
import PivotTableWidget from './PivotTableWidget.vue';
import MatrixTableWidget from './MatrixTableWidget.vue';
import TreeTableWidget from './TreeTableWidget.vue';
import { computed } from 'vue';
import { t, tConfig } from '../i18n/index.js';
import { getTableCellValue } from '../utils/widgetTransform.js';

const props = defineProps({
  widget: { type: Object, required: true },
  lang: { type: String, default: 'ca_es' },
  editable: { type: Boolean, default: true },
  // Opcions ApexCharts ja mesclades (buildMergedOptions) per a widgets chart.
  options: { type: Object, default: () => ({ chart: {} }) },
  // Sèrie ja calculada per a widgets chart.
  series: { type: Array, default: () => [] },
  // Node de dades transformat (chartData[dataSourceKey]): taules, mapes, pivots, arbre.
  dataNode: { type: Object, default: null },
  // Labels i18n dels camps (metaLabelCache[segmentId]) per a tooltips d'agregades.
  metaLabels: { type: Object, default: () => ({}) },
  // Valor escalar del KPI (calculat pel pare, depèn del total del segment).
  kpiValue: { type: [Number, String], default: 0 },
  // Agregada del KPI: { value, label, only }.
  kpiMetric: { type: Object, default: () => ({ value: null, label: '', only: false }) }
});

defineEmits(['data-point-selection', 'export-table']);

// Mapping efectiu: a l'esquema canònic `mapping` només porta `value`; category/group es
// deriven de `dimension` (1a→categoria, 2a→grup). Reprodueix la derivació de widgetTransform
// perquè els renderers de pivot/matrix/mapa (que llegeixen mapping directament) funcionin
// tant amb configs antigues (que SÍ porten category/group) com amb canòniques.
const effMapping = computed(() => {
  const p = props.widget?.params || {};
  const m = { ...(p.mapping || {}) };
  const dims = Array.isArray(p.dimension) ? p.dimension.filter(Boolean) : (p.dimension ? [p.dimension] : []);
  const isTable = props.widget?.type === 'chart' && p.chart?.type === 'table';
  if ((m.category == null || m.category === '') && dims.length) m.category = dims[0];
  if (!isTable && (m.group == null || m.group === '') && dims.length > 1) m.group = dims[1];
  if (m.value == null) m.value = 'count';
  return m;
});
</script>

<style scoped>
/* Vista de taula (widget chart amb chart.type: 'table') amb el mateix estil que les
   taules matrix/pivot. Ha d'anar AQUÍ (no a Dashboard.vue): els estils scoped del pare
   no arriben als elements interns d'un component fill, només a la seva arrel. */
.table-widget {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
  max-height: 480px;
  box-sizing: border-box;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.table-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
}

.table-export-btn {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #059669;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.table-export-btn:hover { background-color: #059669; border-color: #047857; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.data-table th:not(:first-child),
.data-table td:not(:first-child) {
  text-align: right;
  white-space: nowrap;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.data-table tr:hover { background-color: #f9fafb; }
</style>
